import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import ThemeToggle from '../components/ThemeToggle';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name:'', email:'', password:'' });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode==='login') await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate('/');
    } catch (err) { toast.error(err.response?.data?.error || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', background:'var(--bg-secondary)' }}>
      {/* Left panel - branding */}
      <div style={{ flex:1, background:'linear-gradient(135deg,#5865f2 0%,#7c3aed 50%,#4f46e5 100%)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:48, color:'#fff' }}>
        <div style={{ maxWidth:400 }}>
          <BrandLogo height={56} style={{ marginBottom:36 }} />
          <h1 style={{ fontSize:36, fontWeight:800, lineHeight:1.2, marginBottom:16 }}>
            The All-in-One<br />API Development Platform
          </h1>
          <p style={{ fontSize:15, opacity:.85, lineHeight:1.7, marginBottom:32 }}>
            Design, debug, test, mock, and document your APIs in one unified workspace. Built for teams that ship great software.
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {['API Design with OpenAPI 3.0 support','Real-time collaboration','Auto-generated documentation','Mock servers & test runners','Support ticket system'].map(f => (
              <div key={f} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, opacity:.9 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>✓</div>
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div style={{ width:480, display:'flex', alignItems:'center', justifyContent:'center', padding:48, background:'var(--bg)', position:'relative' }}>
        <ThemeToggle style={{ position:'absolute', top:20, right:20 }} />
        <div style={{ width:'100%', maxWidth:380 }}>
          <h2 style={{ fontSize:26, fontWeight:800, marginBottom:6, color:'var(--text-1)' }}>
            {mode==='login' ? 'Welcome back' : 'Create account'}
          </h2>
          <p style={{ fontSize:14, color:'var(--text-3)', marginBottom:28 }}>
            {mode==='login' ? 'Sign in to your workspace' : 'Start building amazing APIs today'}
          </p>

          <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {mode==='register' && (
              <div style={{ position:'relative' }}>
                <User size={15} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)' }} />
                <input style={{ paddingLeft:34 }} type="text" placeholder="Full name" value={form.name} onChange={e=>set('name',e.target.value)} required />
              </div>
            )}
            <div style={{ position:'relative' }}>
              <Mail size={15} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)' }} />
              <input style={{ paddingLeft:34 }} type="email" placeholder="Email address" value={form.email} onChange={e=>set('email',e.target.value)} required />
            </div>
            <div style={{ position:'relative' }}>
              <Lock size={15} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)' }} />
              <input style={{ paddingLeft:34, paddingRight:36 }} type={showPw?'text':'password'} placeholder="Password" value={form.password} onChange={e=>set('password',e.target.value)} required />
              <button type="button" style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', padding:2 }} onClick={() => setShowPw(v=>!v)}>
                {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
            <button type="submit" style={{ background:'var(--accent)', color:'#fff', border:'none', padding:'11px', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontFamily:'var(--font)', transition:'all .15s', marginTop:4 }}
              disabled={loading}>
              {loading ? 'Please wait…' : mode==='login' ? 'Sign In' : 'Create Account'}
              {!loading && <ArrowRight size={15}/>}
            </button>
          </form>

          <div style={{ textAlign:'center', marginTop:20, fontSize:13, color:'var(--text-3)' }}>
            {mode==='login' ? "Don't have an account? " : 'Already have an account? '}
            <button style={{ background:'none', border:'none', color:'var(--accent)', cursor:'pointer', fontWeight:600, fontFamily:'var(--font)', fontSize:13 }}
              onClick={() => setMode(mode==='login'?'register':'login')}>
              {mode==='login' ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
